import React, {useEffect, useMemo, useState} from "react";
import {isDesktop, isMobile, isMobileOnly} from "react-device-detect";
import csvDownload from "json-to-csv-export";
import {
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip
} from "@mui/material";
import {makeStyles} from "@mui/styles";
import {
    Delete as DeleteIcon,
    Download as DownloadIcon,
    Edit as EditIcon,
    Email as EmailIcon,
    Phone as CallIcon,
    Sms as SmsIcon,
    ViewList as TableIcon,
    ViewModule as CardIcon,
} from "@mui/icons-material";
import {useFiltered} from "@atttomyx/shared-hooks";
import {
    Cards,
    ConfirmDeleteDialog,
    FiltersAccordion,
    FloatingAddButton,
    FloatingButton,
    SelectField
} from "@atttomyx/react-components";
import {UserCard} from "../cards";
import {UserFilters} from "../filters";
import {userService} from "../../services";
import {arrays, datetime, formatters, forms, objects, strings, time, users as userUtils} from "@atttomyx/shared-utils";
import {communication} from "@atttomyx/react-utils";
import {formatPhone} from "../../utils/nodes";
import {
    CSV_DELIMITER,
    CSV_FIELD_CREATED,
    CSV_FIELD_DOWNLOADED,
    CSV_FIELD_EMAIL,
    CSV_FIELD_FIRST_NAME,
    CSV_FIELD_LAST_NAME,
    CSV_FIELD_LISTENED,
    CSV_FIELD_LOGIN,
    CSV_FIELD_PHONE,
    CSV_FIELD_TYPE,
    CSV_FIELD_UPDATED,
    CSV_FIELD_UPLOADED,
    CSV_FIELD_WATCHED,
    CSV_FILENAME_USERS,
    CSV_HEADERS_USERS,
    STATUS_ACTIVE,
    TYPE_INVENTOR,
    VIEW_CARDS,
    VIEW_TABLE
} from "../../constants";
import {misc, times} from "@atttomyx/shared-constants";
import "./usersPage.css";

const useStyles = makeStyles(theme => ({
    root: {
        maxWidth: "500px",
        margin: "0 auto",
    },
}));

const toComposite = (questionnaireId, userId) => `${questionnaireId}:${userId}`;

const toIso = (timestamp) => {
    if (!timestamp) {
        return "-";
    }
    return new Date(timestamp).toISOString();
}

const getLastLogin = (timestamp) => {
    if (!timestamp) {
        return "-";
    }
    const millisecondsSince = Date.now() - timestamp;
    const formatted = time.formatRemainingHuge(millisecondsSince);
    const clazz = millisecondsSince > times.month
        ? "inactive"
        : millisecondsSince < times.week
            ? "active"
            : undefined;

    return <span className={clazz}>{formatted}</span>;
}

const UsersPage = (props) => {
    const { dimensions, snackbar, user: me, users, onDelete, onGotoProfile, onGotoUser, onGotoAdd,
        filters, documents, questionnaires, questions, submissions, progresses, questionnaireRenderer} = props;
    const [ user, setUser ] = useState(null);
    const [ showFilters, setShowFilters ] = useState(false);
    const filtered = useFiltered(users, filters, userUtils.sortByName);
    const classes = useStyles();

    const [pages, setPages] = useState([]);
    const [list, setList] = useState([]);

    useEffect(() => {
        const arrays = [];

        for (let i = 0; i < filtered.length; i += filters.paging.perPage) {
            const subArray = filtered.slice(i, i + filters.paging.perPage);

            if (subArray.length > 0) {
                arrays.push(subArray);
            }
        }

        setPages(arrays);
    }, [filtered, filters.paging.perPage]);

    useEffect(() => {
        if (filters.paging.page > -1 && pages.length > filters.paging.page) {
            setList(pages[filters.paging.page]);

        } else if (pages.length > 0) {
            setList(pages[0]);

        } else {
            setList([]);
        }
    }, [pages, filters.paging.page]);

    const documentIdsByUserId = useMemo(() => {
        const documentIds = {};

        documents.forEach(document => {
            if (document.userId) {
                const array = objects.defaultIfNullOrUndefined(documentIds[document.userId], []);

                arrays.addTo(array, document.id);
                documentIds[document.userId] = array;
            }
        });

        return documentIds;
    }, [documents]);

    const idToQuestionnaire = useMemo(() => {
        return arrays.getIdToEntity(questionnaires);
    }, [questionnaires]);

    const idToTitle = useMemo(() => {
        const map = {};

        questionnaires
        .filter(questionnaire => questionnaire.status === STATUS_ACTIVE)
        .forEach(questionnaire => {
            const questionnaireId = questionnaire.id;

            map[questionnaireId] = questionnaireRenderer.render(questionnaireId);
        });

        return map;
    }, [questionnaires]);

    const questionnaire = useMemo(() => {
        return filters.options.questionnaireId
            ? idToQuestionnaire[filters.options.questionnaireId]
            : null;
    }, [idToQuestionnaire, filters.options]);

    const questionsToUse = useMemo(() => {
        const questionnaireId = questionnaire ? questionnaire.id : null;

        return questionnaireId
            ? questions.filter(question => question.questionnaireId === questionnaireId)
            : [];
    }, [questionnaire, questions]);

    const submissionByComposite = useMemo(() => {
        const map = {};

        submissions.forEach(submission => {
            const composite = `${submission.questionnaireId}:${submission.userId}`;

            map[composite] = submission;
        });

        return map;
    }, [submissions]);

    const countsByUserId = useMemo(() => {
        const map = {};

        progresses.forEach(progress => {
            map[progress.userId] = {
                documents: Object.keys(progress.documentIds).length,
                videos: Object.keys(progress.videoIds).length,
                music: objects.notNullOrUndefined(progress.music),
            };
        });

        return map;
    }, [progresses]);

    const getSubmission = (user) => {
        return questionnaire
            ? submissionByComposite[toComposite(questionnaire.id, user.id)]
            : null;
    };

    const makeContact = (userId, method) => {
        if (me.roles.investor) {
            const noop = () => {};

            userService.contacted(userId, noop, noop);
        }

        method();
    };

    const downloadCsv = () => {
        const lines = [];

        filtered.forEach(user => {
            const counts = countsByUserId[user.id] || {};
            const isInventor = user.settings.type === TYPE_INVENTOR;

            arrays.addTo(lines, {
                [CSV_FIELD_FIRST_NAME]: user.firstName,
                [CSV_FIELD_LAST_NAME]: user.lastName,
                [CSV_FIELD_TYPE]: (user.settings.type ? user.settings.type : misc.UNKNOWN).toLowerCase(),
                [CSV_FIELD_EMAIL]: user.email,
                [CSV_FIELD_PHONE]: formatPhone(user.phone),
                [CSV_FIELD_LISTENED]: isInventor ? formatters.booleanToString(counts.music) : "-",
                [CSV_FIELD_WATCHED]: isInventor ? (counts.videos || 0) : "-",
                [CSV_FIELD_DOWNLOADED]: isInventor ? (counts.documents || 0) : "-",
                [CSV_FIELD_UPLOADED]: isInventor ? (documentIdsByUserId[user.id] || []).length : "-",
                [CSV_FIELD_LOGIN]: toIso(user.login),
                [CSV_FIELD_CREATED]: user.created,
                [CSV_FIELD_UPDATED]: user.updated,
            });
        });

        csvDownload({
            data: lines,
            delimiter: CSV_DELIMITER,
            headers: CSV_HEADERS_USERS,
            filename: CSV_FILENAME_USERS,
        });
    };

    const handleChangePerPage = (event) => {
        filters.paging.setPerPage(parseInt(event.target.value, 10));
    };

    const handleChangePage = (event, value) => {
        filters.paging.setPage(value);
    };

    const renderActions = (user) => (
        <>
            {me.roles.admin ?
                <IconButton color="secondary" title="Delete" className="delete"
                            disabled={me.id === user.id}
                            onClick={() => setUser(user)}>
                    <DeleteIcon/>
                </IconButton> : null}
            {isMobileOnly ?
                <IconButton color="primary" title="Call"
                            disabled={me.id === user.id || strings.isBlank(user.phone)}
                            onClick={() => makeContact(user.id, () => communication.popCall(user.phone))}>
                    <CallIcon/>
                </IconButton> : null}
            {isMobile ?
                <IconButton color="primary" title="Text"
                            disabled={me.id === user.id || strings.isBlank(user.phone)}
                            onClick={() => makeContact(user.id, () => communication.popSms(user.phone))}>
                    <SmsIcon/>
                </IconButton> : null}
            <IconButton color="primary" title="Email"
                        disabled={me.id === user.id}
                        onClick={() => makeContact(user.id, () => communication.popEmail(user.email))}>
                <EmailIcon/>
            </IconButton>
            {me.roles.admin ?
                <IconButton color="primary" title="Edit"
                            onClick={() => me.id === user.id ? onGotoProfile() : onGotoUser(user.id)}>
                    <EditIcon/>
                </IconButton> : null}
        </>
    );

    const pagination = <TablePagination
        classes={classes}
        component="div"
        count={filtered.length}
        labelRowsPerPage={dimensions.wide ? "per page" : ""}
        rowsPerPage={filters.paging.perPage}
        onRowsPerPageChange={handleChangePerPage}
        page={filters.paging.page >= pages.length ? 0 : filters.paging.page}
        onPageChange={handleChangePage}
        showFirstButton={dimensions.wide}
        showLastButton={dimensions.wide}
    />

    return <div className="users-page">
        {isDesktop ?
            <div className="toggle">
                <ToggleButtonGroup
                    value={forms.sanitizeValue(filters.options.view)}
                    exclusive
                    onChange={(event, view) => filters.setOptions({
                        view: view,
                    })}
                    aria-label="view mode"
                >
                    <Tooltip title="Cards">
                        <ToggleButton value={VIEW_CARDS}>
                            <CardIcon color={filters.options.view !== VIEW_TABLE ? "secondary" : undefined}/>
                        </ToggleButton>
                    </Tooltip>
                    <Tooltip title="Table">
                        <ToggleButton value={VIEW_TABLE}>
                            <TableIcon color={filters.options.view === VIEW_TABLE ? "secondary" : undefined}/>
                        </ToggleButton>
                    </Tooltip>
                </ToggleButtonGroup>
            </div> : null}
        <div className="field">
            <SelectField
                label="Form"
                value={forms.sanitizeValue(filters.options.questionnaireId)}
                onChange={(questionnaireId) => filters.setOptions({
                    questionnaireId: questionnaireId,
                })}
                options={idToTitle}
            />
        </div>
        <div className="field">
            <FiltersAccordion
                filters={filters}
                form={UserFilters}
                open={showFilters}
                onOpen={() => setShowFilters(true)}
                onClose={() => setShowFilters(false)}
                formProps={{
                    user: me,
                    questions: questionsToUse,
                }}
            />
        </div>
        {filters.options.view !== VIEW_TABLE ? (
            <Cards
                items={filtered}
                paging={filters.paging}
                dimensions={dimensions}
                renderer={(user) =>
                    <UserCard key={user.id} user={user} me={me}
                              documentIdsByUserId={documentIdsByUserId}
                              questionnaire={questionnaire}
                              submission={getSubmission(user)}
                              counts={countsByUserId[user.id]}
                              filters={filters}
                    >
                        {renderActions(user)}
                    </UserCard>}
            />
        ) : (
            <>
                {pagination}
                <TableContainer component={Paper}>
                    <Table className="striped data-table">
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Phone</TableCell>
                                <TableCell>Login</TableCell>
                                <TableCell>Listened</TableCell>
                                <TableCell>Watched</TableCell>
                                <TableCell>Downloaded</TableCell>
                                <TableCell>Uploaded</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell>Updated</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {list.map((user) => {
                                const counts = countsByUserId[user.id] || {};
                                const isInventor = user.settings.type === TYPE_INVENTOR;

                                return (
                                    <TableRow key={user.id}>
                                        <TableCell>{userUtils.getFullName(user)}</TableCell>
                                        <TableCell>{(user.settings.type ? user.settings.type : misc.UNKNOWN).toLowerCase()}</TableCell>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>{formatPhone(user.phone)}</TableCell>
                                        <TableCell>{getLastLogin(user.login)}</TableCell>
                                        <TableCell>{isInventor ? formatters.booleanToString(counts.music) : "-"}</TableCell>
                                        <TableCell>{isInventor ? (counts.videos || 0) : "-"}</TableCell>
                                        <TableCell>{isInventor ? (counts.documents || 0) : "-"}</TableCell>
                                        <TableCell>{isInventor ? (documentIdsByUserId[user.id] || []).length : "-"}</TableCell>
                                        <TableCell>{datetime.getFullDateAndTime(user.created)}</TableCell>
                                        <TableCell>{datetime.getFullDateAndTime(user.updated)}</TableCell>
                                        <TableCell>{renderActions(user)}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </>
        )}
        {me.roles.admin ?
            <FloatingButton
                className="floating-download"
                icon={<DownloadIcon/>}
                title="Download CSV"
                position={dimensions.portrait ? "higher" : undefined}
                center={false}
                onClick={downloadCsv}
                disabled={!isDesktop}
            /> : null}
        {me.roles.admin ?
            <FloatingAddButton
                title="Add user"
                position={dimensions.portrait ? "higher" : undefined}
                center={false}
                onClick={onGotoAdd}
            /> : null}
        {user ? <ConfirmDeleteDialog
            snackbar={snackbar}
            title={userUtils.getFullName(user)}
            onCancel={() => setUser(null)}
            onDelete={(userId) => {
                setUser(null);
                onDelete(userId);
            }}
            deleter={(success, failure) => userService.softDeleteUser(user.id, success, failure)}
        /> : null}
    </div>
}

export default UsersPage;
